import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { DropdownModule } from "primeng/dropdown";
import { CalendarModule } from "primeng/calendar";
import { CardModule } from "primeng/card";
import { ButtonModule } from "primeng/button";
import { ToastModule } from "primeng/toast";
import { InputSwitchModule } from "primeng/inputswitch";
import { DialogModule } from "primeng/dialog";
import { SelectButtonModule } from "primeng/selectbutton";
import { InputTextModule } from "primeng/inputtext";
import { MessageService } from "primeng/api";
import { firstValueFrom } from "rxjs";
import { CourtService } from "../../services/court.service";
import { CourtReservationService } from "../../services/court-reservation.service";
import { AuthenticationService } from "../../services/authentication.service";
import { UserService } from "../../services/user.service";
import { ClubService } from "../../services/club.service";

@Component({
  selector: "app-manual-book",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    CalendarModule,
    CardModule,
    ButtonModule,
    ToastModule,
    InputSwitchModule,
    DialogModule,
    SelectButtonModule,
    InputTextModule,
  ],
  providers: [MessageService],
  templateUrl: "./manual-book.component.html",
  styleUrl: "./manual-book.component.css",
})
export class ManualBookComponent implements OnInit {
  courts: any[] = [];
  availableCourts: any[] = [];
  times: { hour: number; available: boolean }[] = [];
  clubReservations: any[] = [];
  selectedClub: any = null;
  selectedCourt: any = null;
  selectedTimes: number[] = [];
  reservationDate: Date | null = null;
  verified = true;
  matchType: "single" | "double" = "double";
  isPrivate = false;
  player1Name = "";
  player2Name = "";
  player3Name = "";
  player4Name = "";
  bookerIndex = 1;
  allUsers: any[] = [];
  filteredUsers1: string[] = [];
  filteredUsers2: string[] = [];
  filteredUsers3: string[] = [];
  filteredUsers4: string[] = [];
  showAdd1 = false;
  showAdd2 = false;
  showAdd3 = false;
  showAdd4 = false;
  addDialogVisible = false;
  creatingUser = false;
  currentField = 1;
  newPlayer: any = {
    name: "",
    last_name: "",
    email: "",
    phone_number: "",
  };
  matchTypeOptions: {
    label: string;
    value: "single" | "double";
    disabled?: boolean;
  }[] = [
    { label: "Double", value: "double" },
    { label: "Single", value: "single", disabled: true },
  ];
  availableMatchTypeOptions = [...this.matchTypeOptions];
  singleSupported = false;
  minDate: Date = new Date();
  clubWorkingHoursDisplay = "";
  clubMinPrice = 0;
  totalPrice = 0;
  clubInfoVisible = false;
  dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  @ViewChild("clubDetails") clubDetails?: ElementRef<HTMLElement>;
  isLoading = true;
  reservationMode: "basic" | "advanced" = "basic";
  reservationModeOptions = [
    { label: "Basic", value: "basic" },
    { label: "Advanced", value: "advanced" },
  ];
  basicPlayerFullName = "";
  basicPlayerEmail = "";
  basicPlayerPhone = "";
  addEmail = false;
  emailDialogVisible = false;
  emailInput = "";

  constructor(
    private courtService: CourtService,
    private reservationService: CourtReservationService,
    private auth: AuthenticationService,
    private userService: UserService,
    private clubService: ClubService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    const current = this.auth.currentUserValue;
    if (!current) {
      this.verified = false;
      this.isLoading = false;
      return;
    }

    this.userService.getUser(current.id).subscribe({
      next: (u) => {
        this.verified = u.roles.find((x: { id: number }) => x.id == 2);
        if (this.verified) {
          const clubId = u.club?.id ?? u.club_id;
          if (clubId) {
            this.clubService.getClubs().subscribe((clubs) => {
              this.selectedClub = clubs.find((c) => c.id === clubId) || null;
              if (this.selectedClub) {
                this.onClubChange();
              }
            });
          }
        }
        this.loadUsers();
        this.isLoading = false;
      },
      error: () => {
        this.verified = false;
        this.isLoading = false;
      },
    });
  }

  selectClub(club: any) {
    this.selectedClub = club;
    this.onClubChange();
    this.computeClubInfo();
    setTimeout(() => {
      this.clubDetails?.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  }

  openClubInfo() {
    this.clubInfoVisible = true;
  }

  onReservationModeChange() {
    if (this.reservationMode === "basic") {
      this.player1Name =
        this.player2Name =
        this.player3Name =
        this.player4Name =
          "";
      this.basicPlayerFullName = "";
      this.basicPlayerEmail = "";
      this.basicPlayerPhone = "";
      this.addEmail = false;
    }
  }

  onEmailSwitchChange(event: any) {
    if (event.checked) {
      this.emailInput = this.basicPlayerEmail;
      this.emailDialogVisible = true;
    } else {
      this.basicPlayerEmail = "";
    }
  }

  saveEmail() {
    if (!this.isValidEmail(this.emailInput)) {
      this.messageService.add({
        severity: "warn",
        summary: "Invalid email",
        detail: "Please enter a valid email",
      });
      return;
    }
    this.basicPlayerEmail = this.emailInput;
    this.emailDialogVisible = false;
  }

  cancelEmail() {
    this.emailDialogVisible = false;
    this.addEmail = false;
    this.basicPlayerEmail = "";
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  onClubChange() {
    this.selectedCourt = null;
    this.availableCourts = [];
    this.selectedTimes = [];
    this.times = [];
    this.reservationDate = null;
    this.clubReservations = [];
    this.matchType = "double";
    this.singleSupported = false;
    this.updateMatchTypeOptions();
    if (this.selectedClub) {
      this.courtService
        .getCourtsForClub(this.selectedClub.id)
        .subscribe((res) => {
          this.courts = res;
          this.singleSupported = this.courts.some(
            (c) => (c.match_type_mask & 1) !== 0,
          );
          this.updateMatchTypeOptions();
          this.matchType = "double";
        });
      this.computeClubInfo();
    } else {
      this.courts = [];
      this.singleSupported = false;
      this.updateMatchTypeOptions();
      this.matchType = "double";
    }
    this.totalPrice = 0;
  }

  onDateChange() {
    this.selectedTimes = [];
    this.availableCourts = [];
    this.times = [];
    this.clubReservations = [];
    if (this.selectedClub && this.reservationDate) {
      const local = new Date(
        this.reservationDate.getTime() -
          this.reservationDate.getTimezoneOffset() * 60000,
      );
      const dateStr = local.toISOString().split("T")[0];
      this.reservationService
        .getReservationsForClub(this.selectedClub.id, dateStr)
        .subscribe((res) => {
          this.clubReservations = res;
          this.generateAvailableTimes();
          this.computeTotalPrice();
        });
    }
  }

  private generateAvailableTimes() {
    if (!this.reservationDate) return;
    const intervals = this.computeOpeningIntervals(this.reservationDate);
    const now = new Date();
    const isToday =
      this.reservationDate.getFullYear() === now.getFullYear() &&
      this.reservationDate.getMonth() === now.getMonth() &&
      this.reservationDate.getDate() === now.getDate();
    for (const interval of intervals) {
      const start = Math.ceil(interval.start);
      const end = Math.floor(interval.end);
      for (let i = start; i < end; i++) {
        const reservationsAtHour = this.clubReservations.filter((r) => {
          const s = new Date(r.reservation_start_time).getHours();
          const e = new Date(r.reservation_end_time).getHours();
          return i >= s && i < e;
        });
        const reservedCourts = new Set(
          reservationsAtHour.map((r: any) => r.court_id),
        );
        let available =
          reservedCourts.size < this.selectedClub.number_of_courts;
        if (isToday && i <= now.getHours()) {
          available = false;
        }
        this.times.push({ hour: i, available });
      }
    }
  }

  selectTime(hour: number) {
    const time = this.times.find((t) => t.hour === hour);
    if (!time?.available) {
      return;
    }
    if (this.selectedTimes.includes(hour)) {
      this.selectedTimes = this.selectedTimes.filter((h) => h !== hour);
    } else {
      this.selectedTimes.push(hour);
      this.selectedTimes.sort((a, b) => a - b);
    }
    this.computeAvailableCourts();
    this.computeTotalPrice();
  }

  private computeAvailableCourts() {
    if (this.selectedTimes.length === 0) {
      this.availableCourts = [];
      return;
    }
    const reservedCourtIds = new Set(
      this.clubReservations
        .filter((r) => {
          const s = new Date(r.reservation_start_time).getHours();
          const e = new Date(r.reservation_end_time).getHours();
          return this.selectedTimes.some((t) => t >= s && t < e);
        })
        .map((r: any) => r.court_id),
    );
    const required = this.matchType === "single" ? 1 : 2;
    this.availableCourts = this.courts.filter(
      (c) =>
        !reservedCourtIds.has(c.id) &&
        (c.match_type_mask & required) === required,
    );
    if (this.selectedClub.id === 8 && this.reservationDate) {
      const dow = this.reservationDate.getDay();
      if (dow >= 1 && dow <= 5) {
        this.availableCourts = this.availableCourts.filter(
          (c) => c.name === "Padel Bros court 1",
        );
      }
    }
    this.selectedCourt = null;
  }

  onMatchTypeChange() {
    if (this.matchType === "single" && !this.singleSupported) {
      this.matchType = "double";
      return;
    }
    this.computeAvailableCourts();
    this.computeTotalPrice();
  }

  private updateMatchTypeOptions() {
    this.availableMatchTypeOptions = this.matchTypeOptions.map((o) =>
      o.value === "single" ? { ...o, disabled: !this.singleSupported } : o,
    );
  }

  private getHour(ts: any): number {
    if (!ts) return 0;
    if (typeof ts === "string") {
      const parts = ts.split(":");
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      return h + m / 60;
    }
    if (typeof ts === "object" && "hours" in ts) {
      return ts.hours + (ts.minutes || 0) / 60;
    }
    const num = Number(ts);
    return isNaN(num) ? 0 : num;
  }

  private computeOpeningIntervals(
    date: Date,
    club: any = this.selectedClub,
  ): { start: number; end: number }[] {
    if (!club) return [];
    const dow = date.getDay();
    let intervals = (club.clubOpeningHours || [])
      .filter((o: any) => o.day_of_week === dow)
      .map((o: any) => ({
        start: this.getHour(o.start_time),
        end: this.getHour(o.end_time),
      }));
    const exc = (club.clubOpeningExceptions || []).find((e: any) => {
      const d = new Date(e.date_);
      return (
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
      );
    });
    if (exc) {
      if (exc.is_closed) return [];
      const excStart = this.getHour(exc.start_time);
      const excEnd = this.getHour(exc.end_time);
      intervals = intervals.flatMap((int: any) => {
        if (excEnd <= int.start || excStart >= int.end) return [int];
        const res: { start: number; end: number }[] = [];
        if (excStart > int.start)
          res.push({ start: int.start, end: Math.min(excStart, int.end) });
        if (excEnd < int.end)
          res.push({ start: Math.max(excEnd, int.start), end: int.end });
        return res;
      });
    }
    return intervals;
  }

  private formatHour(h: number): string {
    return Math.floor(h).toString().padStart(2, "0") + ":00";
  }

  private formatIntervals(intervals: { start: number; end: number }[]): string {
    return intervals
      .map((i) => `${this.formatHour(i.start)}-${this.formatHour(i.end)}`)
      .join(", ");
  }

  private computeClubInfo() {
    if (!this.selectedClub) {
      this.clubWorkingHoursDisplay = "";
      this.clubMinPrice = 0;
      return;
    }
    const today = new Date();
    const intervals = this.computeOpeningIntervals(today);
    this.clubWorkingHoursDisplay = intervals.length
      ? this.formatIntervals(intervals)
      : "Closed";
    this.clubMinPrice = this.getMinPrice(today);
  }

  getClubWorkingHoursDisplay(club: any): string {
    const today = new Date();
    const intervals = this.computeOpeningIntervals(today, club);
    return intervals.length ? this.formatIntervals(intervals) : "Closed";
  }

  getClubMinPrice(club: any): number {
    const today = new Date();
    return this.getMinPrice(today, club);
  }

  formatTime(ts: any): string {
    const h = this.getHour(ts);
    const hour = Math.floor(h);
    const min = Math.round((h - hour) * 60);
    return (
      hour.toString().padStart(2, "0") + ":" + min.toString().padStart(2, "0")
    );
  }
  private formatDayRange(days: number[]): string {
    if (days.length === 7) {
      return "Every day";
    }
    const sorted = [...days].sort((a, b) => a - b);
    const ranges: string[] = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const cur = sorted[i];
      if (cur === prev + 1) {
        prev = cur;
        continue;
      }
      ranges.push(
        start === prev
          ? this.dayNames[start]
          : `${this.dayNames[start]}-${this.dayNames[prev]}`,
      );
      start = prev = cur;
    }
    ranges.push(
      start === prev
        ? this.dayNames[start]
        : `${this.dayNames[start]}-${this.dayNames[prev]}`,
    );
    return ranges.join(", ");
  }

  getAllOpeningHours(club: any): string[] {
    const groups: { [key: string]: number[] } = {};
    const daysWithHours = new Set<number>();
    for (const o of club.clubOpeningHours || []) {
      const interval = `${this.formatTime(o.start_time)}-${this.formatTime(
        o.end_time,
      )}`;
      groups[interval] = groups[interval] || [];
      groups[interval].push(o.day_of_week);
      daysWithHours.add(o.day_of_week);
    }
    const closed: number[] = [];
    for (let i = 0; i < 7; i++) {
      if (!daysWithHours.has(i)) closed.push(i);
    }
    if (closed.length) {
      groups["Closed"] = closed;
    }
    return Object.entries(groups).map(([interval, days]) =>
      interval === "Closed"
        ? `${this.formatDayRange(days)}: Closed`
        : `${this.formatDayRange(days)}: ${interval}`,
    );
  }

  getPricingHoursDisplay(club: any): string[] {
    const groups: { [key: string]: number[] } = {};
    for (const p of club.clubPricingHours || []) {
      const interval = `${this.formatTime(p.start_time)}-${this.formatTime(
        p.end_time,
      )}`;
      const key = `${interval}|${p.price_rsds}`;
      groups[key] = groups[key] || [];
      groups[key].push(p.day_of_week);
    }
    return Object.entries(groups).map(([key, days]) => {
      const [interval, price] = key.split("|");
      return `${this.formatDayRange(days)} ${interval}: ${price} RSD/h`;
    });
  }

  getBundleDisplay(club: any): string[] {
    const groups: { [key: string]: number[] } = {};
    for (const b of club.clubPricingBundles || []) {
      const timePart =
        b.start_time && b.end_time
          ? `${this.formatTime(b.start_time)}-${this.formatTime(b.end_time)}`
          : "";
      const key = `${b.bundle_hours}|${b.price_total_rsds}|${timePart}`;
      groups[key] = groups[key] || [];
      if (b.day_of_week == null) {
        groups[key] = [0, 1, 2, 3, 4, 5, 6];
      } else {
        groups[key].push(b.day_of_week);
      }
    }
    return Object.entries(groups).map(([key, days]) => {
      const [hours, price, time] = key.split("|");
      const dayStr = this.formatDayRange(days);
      const timeStr = time ? ` ${time}` : "";
      return `${dayStr} ${hours}h${timeStr} - ${price} RSD`;
    });
  }

  formatException(e: any): string {
    const date = new Date(e.date_).toLocaleDateString();
    if (e.is_closed) {
      return `${date}: Closed`;
    }
    return `${date}: ${this.formatTime(e.start_time)}-${this.formatTime(
      e.end_time,
    )}`;
  }

  private getMinPrice(date: Date, club: any = this.selectedClub): number {
    if (!club) return 0;
    const dow = date.getDay();
    const pricing = (club.clubPricingHours || []).filter(
      (p: any) => p.day_of_week === dow,
    );
    if (pricing.length) {
      return Math.min(...pricing.map((p: any) => p.price_rsds));
    }
    return club.price_per_hour;
  }

  private computeTotalPrice() {
    if (
      !this.selectedClub ||
      !this.reservationDate ||
      this.selectedTimes.length === 0
    ) {
      this.totalPrice = 0;
      return;
    }
    const groups = this.groupSelectedTimes();
    let total = 0;
    for (const g of groups) {
      const startHour = g[0];
      const duration = g.length;
      const start = new Date(this.reservationDate);
      start.setHours(startHour, 0, 0, 0);
      total += this.calculatePrice(start, duration);
    }
    this.totalPrice = total;
  }

  private calculatePrice(start: Date, duration: number): number {
    const dow = start.getDay();
    const t = start.getHours() + start.getMinutes() / 60;
    const pricing = (this.selectedClub.clubPricingHours || []).find(
      (p: any) =>
        p.day_of_week === dow &&
        t >= this.getHour(p.start_time) &&
        t < this.getHour(p.end_time),
    );
    const hourly = pricing
      ? pricing.price_rsds
      : this.selectedClub.price_per_hour;
    const bundle = (this.selectedClub.clubPricingBundles || []).find(
      (b: any) =>
        b.bundle_hours === duration &&
        (b.day_of_week == null || b.day_of_week === dow) &&
        (!b.start_time || t >= this.getHour(b.start_time)) &&
        (!b.end_time || t < this.getHour(b.end_time)),
    );
    const totalHourly = hourly * duration;
    return bundle
      ? Math.min(bundle.price_total_rsds, totalHourly)
      : totalHourly;
  }

  private sanitizeUsername(name: string): string {
    return name.trim().replace(/^@/, "").toLowerCase();
  }

  private groupSelectedTimes(): number[][] {
    const times = [...this.selectedTimes].sort((a, b) => a - b);
    const groups: number[][] = [];
    let current: number[] = [];
    for (const t of times) {
      if (!current.length || t === current[current.length - 1] + 1) {
        current.push(t);
      } else {
        groups.push(current);
        current = [t];
      }
    }
    if (current.length) groups.push(current);
    return groups;
  }

  private generateRandomPassword(): string {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const special = "!@#$%^&*()_+[]{}|;:,.<>?";

    const pick = (chars: string, count: number) =>
      Array.from({ length: count }, () =>
        chars.charAt(Math.floor(Math.random() * chars.length)),
      ).join("");

    let password =
      pick(upper, 2) + pick(special, 1) + pick(numbers, 3) + pick(lower, 3);

    password = password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
    return password;
  }

  private loadUsers() {
    this.userService.getUsers().subscribe((res) => {
      // only keep users who have no elevated roles (admins/employees)
      this.allUsers = res.filter(
        (u: any) => !u.roles || u.roles.every((r: any) => r.id === 1),
      );
    });
  }

  searchUsers(query: string, field: number) {
    const q = (query || "").toLowerCase();
    const filtered = this.allUsers
      .filter(
        (u) =>
          (u.username && u.username.toLowerCase().includes(q)) ||
          `${u.name} ${u.last_name}`.toLowerCase().includes(q),
      )
      .map((u) => u.username);
    switch (field) {
      case 1:
        this.filteredUsers1 = filtered;
        this.showAdd1 = filtered.length === 0 && q.length > 0;
        break;
      case 2:
        this.filteredUsers2 = filtered;
        this.showAdd2 = filtered.length === 0 && q.length > 0;
        break;
      case 3:
        this.filteredUsers3 = filtered;
        this.showAdd3 = filtered.length === 0 && q.length > 0;
        break;
      case 4:
        this.filteredUsers4 = filtered;
        this.showAdd4 = filtered.length === 0 && q.length > 0;
        break;
    }
  }

  openAddDialog(field: number) {
    this.currentField = field;
    this.newPlayer = {
      name: "",
      last_name: "",
      email: "",
      phone_number: "",
    };
    this.addDialogVisible = true;
  }

  closeAddDialog() {
    this.addDialogVisible = false;
  }

  selectSuggestion(username: string, field: number) {
    switch (field) {
      case 1:
        this.player1Name = username;
        this.filteredUsers1 = [];
        break;
      case 2:
        this.player2Name = username;
        this.filteredUsers2 = [];
        break;
      case 3:
        this.player3Name = username;
        this.filteredUsers3 = [];
        break;
      case 4:
        this.player4Name = username;
        this.filteredUsers4 = [];
        break;
    }
  }

  createNewPlayer() {
    this.creatingUser = true;
    const body = {
      ...this.newPlayer,
      username: "",
      password: this.generateRandomPassword(),
    } as any;
    this.userService
      .createUser(body)
      .subscribe({
        next: (u) => {
          this.allUsers.push(u);
          const username = u.username;
          if (this.currentField === 1) this.player1Name = username;
          if (this.currentField === 2) this.player2Name = username;
          if (this.currentField === 3) this.player3Name = username;
          if (this.currentField === 4) this.player4Name = username;
          this.addDialogVisible = false;
          this.messageService.add({
            severity: "success",
            summary: "User created",
            detail: "Player successfully created",
          });
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to create user",
          });
        },
      })
      .add(() => {
        this.creatingUser = false;
      });
  }

  async bookCourt() {
    if (
      !this.selectedCourt ||
      !this.reservationDate ||
      this.selectedTimes.length === 0
    )
      return;
    for (const t of this.selectedTimes) {
      const start = new Date(this.reservationDate);
      start.setHours(t, 0, 0, 0);
      if (start < new Date()) {
        this.messageService.add({
          severity: "warn",
          summary: "Invalid time",
          detail: "Cannot book a court in the past",
        });
        return;
      }
    }
    if (this.reservationMode === "basic") {
      const name = this.basicPlayerFullName.trim();
      if (!name) {
        this.messageService.add({
          severity: "warn",
          summary: "Missing name",
          detail: "Player (Full Name) is required",
        });
        return;
      }
      if (!this.basicPlayerPhone.trim()) {
        this.messageService.add({
          severity: "warn",
          summary: "Missing phone number",
          detail: "Phone Number is required",
        });
        return;
      }
      if (this.basicPlayerEmail && !this.isValidEmail(this.basicPlayerEmail)) {
        this.messageService.add({
          severity: "warn",
          summary: "Invalid email",
          detail: "Please enter a valid email",
        });
        return;
      }
      try {
        const groups = this.groupSelectedTimes();
        for (const g of groups) {
          const startHour = g[0];
          const duration = g.length;
          const start = new Date(this.reservationDate);
          start.setHours(startHour, 0, 0, 0);
          const startLocal = new Date(
            start.getTime() - start.getTimezoneOffset() * 60000,
          );
          const endLocal = new Date(
            startLocal.getTime() + duration * 60 * 60 * 1000,
          );
          const [firstName, ...lastParts] = name.split(" ").filter((p) => p);
          const lastName = lastParts.join(" ");
          const body: any = {
            club_id: this.selectedClub.id,
            court_id: this.selectedCourt.id,
            user_id: this.auth.currentUserValue?.id,
            reservation_start_time: startLocal.toISOString(),
            reservation_end_time: endLocal.toISOString(),
            started: false,
            finished: false,
            reservation_status: "Approved",
            is_private: this.isPrivate,
            match_type: this.matchType,
            sets: [-1, -1, -1, -1, -1, -1],
            reservation_type: this.reservationMode,
            courtReservationPhoneCallPlayer: [
              {
                court_reservation_id: 0,
                firstName: firstName,
                lastName: lastName,
                phoneNumber: this.basicPlayerPhone,
                email:
                  this.addEmail && this.basicPlayerEmail
                    ? this.basicPlayerEmail
                    : null,
              },
            ],
          };
          await firstValueFrom(
            this.reservationService.addCourtReservation(body),
          );
        }

        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: "Reservation approved",
        });
        this.isPrivate = false;
        this.selectedCourt = null;
        this.selectedTimes = [];
        this.reservationDate = null;
        this.times = [];
        this.availableCourts = [];
        this.totalPrice = 0;
        this.basicPlayerFullName = "";
        this.basicPlayerEmail = "";
        this.basicPlayerPhone = "";
        this.addEmail = false;
      } catch (error) {
        console.error("Failed to book court (basic player):", error);
        console.error("Error details:", JSON.stringify(error, null, 2));
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: "Failed to book court",
        });
      }
      return;
    }

    const players: any[] = [];
    let bookerId: number | null = null;
    if (this.matchType === "single") {
      const n1 = this.sanitizeUsername(this.player1Name);
      const n2 = this.sanitizeUsername(this.player2Name);

      if (!n1 || !n2) {
        this.messageService.add({
          severity: "warn",
          summary: "Missing players",
          detail: "Both player usernames are required",
        });
        return;
      }

      if (new Set([n1, n2]).size !== 2) {
        this.messageService.add({
          severity: "warn",
          summary: "Invalid players",
          detail: "Duplicate player usernames are not allowed",
        });
        return;
      }

      const [p1, p2] = await Promise.all([
        firstValueFrom(this.userService.getUserByUsername(n1)),
        firstValueFrom(this.userService.getUserByUsername(n2)),
      ]);

      if (!p1 || !p2) {
        this.messageService.add({
          severity: "warn",
          summary: "User not found",
          detail: "One or more players not found",
        });
        return;
      }

      players.push({
        court_reservation_id: 0,
        player_id: p1.id,
        position: 0,
        player: {
          id: p1.id,
          full_name: `${p1.name} ${p1.last_name}`,
          email: p1.email,
        },
      });
      players.push({
        court_reservation_id: 0,
        player_id: p2.id,
        position: 3,
        player: {
          id: p2.id,
          full_name: `${p2.name} ${p2.last_name}`,
          email: p2.email,
        },
      });
      bookerId = this.bookerIndex === 1 ? p1.id : p2.id;
    }

    if (this.matchType === "double") {
      const n1 = this.sanitizeUsername(this.player1Name);
      const n2 = this.sanitizeUsername(this.player2Name);
      const n3 = this.sanitizeUsername(this.player3Name);
      const n4 = this.sanitizeUsername(this.player4Name);

      const names = [n1, n2, n3, n4];
      if (names.some((n) => !n)) {
        this.messageService.add({
          severity: "warn",
          summary: "Missing players",
          detail: "All player usernames are required",
        });
        return;
      }

      if (new Set(names).size !== names.length) {
        this.messageService.add({
          severity: "warn",
          summary: "Invalid players",
          detail: "Duplicate player usernames are not allowed",
        });
        return;
      }

      const [p1, p2, p3, p4] = await Promise.all([
        firstValueFrom(this.userService.getUserByUsername(n1)),
        firstValueFrom(this.userService.getUserByUsername(n2)),
        firstValueFrom(this.userService.getUserByUsername(n3)),
        firstValueFrom(this.userService.getUserByUsername(n4)),
      ]);

      if (!p1 || !p2 || !p3 || !p4) {
        this.messageService.add({
          severity: "warn",
          summary: "User not found",
          detail: "One or more players not found",
        });
        return;
      }

      players.push({
        court_reservation_id: 0,
        player_id: p1.id,
        position: 0,
        player: {
          id: p1.id,
          full_name: `${p1.name} ${p1.last_name}`,
          email: p1.email,
        },
      });
      players.push({
        court_reservation_id: 0,
        player_id: p2.id,
        position: 1,
        player: {
          id: p2.id,
          full_name: `${p2.name} ${p2.last_name}`,
          email: p2.email,
        },
      });
      players.push({
        court_reservation_id: 0,
        player_id: p3.id,
        position: 3,
        player: {
          id: p3.id,
          full_name: `${p3.name} ${p3.last_name}`,
          email: p3.email,
        },
      });
      players.push({
        court_reservation_id: 0,
        player_id: p4.id,
        position: 4,
        player: {
          id: p4.id,
          full_name: `${p4.name} ${p4.last_name}`,
          email: p4.email,
        },
      });
      const map = [p1.id, p2.id, p3.id, p4.id];
      bookerId = map[this.bookerIndex - 1];
    }

    try {
      const groups = this.groupSelectedTimes();
      for (const g of groups) {
        const startHour = g[0];
        const duration = g.length;
        const start = new Date(this.reservationDate);
        start.setHours(startHour, 0, 0, 0);
        const startLocal = new Date(
          start.getTime() - start.getTimezoneOffset() * 60000,
        );
        const endLocal = new Date(
          startLocal.getTime() + duration * 60 * 60 * 1000,
        );

        const body = {
          club_id: this.selectedClub.id,
          court_id: this.selectedCourt.id,
          user_id: bookerId ?? this.auth.currentUserValue?.id,
          reservation_start_time: startLocal.toISOString(),
          reservation_end_time: endLocal.toISOString(),
          started: false,
          finished: false,
          reservation_status: "Approved",
          is_private: this.isPrivate,
          match_type: this.matchType,
          sets: [-1, -1, -1, -1, -1, -1],
          courtReservationPlayer: players,
          reservation_type: this.reservationMode,
        } as any;
        await firstValueFrom(this.reservationService.addCourtReservation(body));
      }

      this.messageService.add({
        severity: "success",
        summary: "Success",
        detail: "Reservation approved",
      });
      this.isPrivate = false;
      this.selectedCourt = null;
      this.selectedTimes = [];
      this.reservationDate = null;
      this.times = [];
      this.availableCourts = [];
      this.totalPrice = 0;
      this.player1Name = "";
      this.player2Name = "";
      this.player3Name = "";
      this.player4Name = "";
    } catch (error) {
      console.error("Failed to book court (registered players):", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to book court",
      });
    }
  }

  openMaps(location: string) {
    const url =
      "https://www.google.com/maps/dir/?api=1&destination=" +
      encodeURIComponent(location);
    window.open(url, "_blank");
  }
}
